// rounds/configuration types — the builder's step vocabulary.
//
// Lives in types/ rather than with `useWizardSteps` for a BUNDLE reason, not a tidiness one.
//
// The route file needs `toBuilderView` to validate `?step=` in `validateSearch`, which runs during
// routing — before the lazily-split screen component loads. When these lived in hooks/, that import
// reached the feature barrel and pulled the hooks module, which imports `@tanstack/react-form`. The
// result was 45 kB gzip of wizard and form library preloaded on FIRST PAINT, including on the login
// page, to reach a four-line string narrowing.
//
// Nothing here imports React or Zod, so a route can take it for free.

/**
 * The FIVE steps the stepper shows, in order (Figma 02 · 408:21840).
 *
 * Grown from four, and the change is not additive — the shape of the flow moved:
 *
 *   - **« Communications » (4) and « Jalons » (5) are new.** Both already have backend contracts
 *     (`/communications/`, `/template-assignments/`, `/milestones/`), so they are frontend gaps
 *     rather than blocked work. Jalons is US-E1-14, a NEW P0 story whose whole point is that a
 *     milestone may be an exact date OR an interval from closing — which the serializer already
 *     models as `exact_at` vs `offset_value`.
 *   - **« Révision » is gone as a step.** Readiness and publishing are now a persistent header
 *     action (« Vérifier et planifier »), reachable from any step rather than only after walking to
 *     the end. It survives as a `BuilderView` below.
 */
export const WIZARD_STEPS = [
  "general",
  "eligibility",
  "questions",
  "communications",
  "milestones",
] as const;

export type WizardStep = (typeof WIZARD_STEPS)[number];

/**
 * The steps the design marks « · optionnel ».
 *
 * They are optional to REACH THE END of, not optional to configure — US-E1-06's readiness check
 * still refuses to schedule a round whose message assignments have a gap ("a moment that sends a
 * message must not be left without one assigned"). The marker sets expectations about the walk
 * through the builder; the gate is `/readiness/`, and it is the authority.
 */
export const OPTIONAL_STEPS: readonly WizardStep[] = [
  "communications",
  "milestones",
];

export function isOptionalStep(step: WizardStep): boolean {
  return OPTIONAL_STEPS.includes(step);
}

/**
 * Everything the builder's body can show — the five steps plus `review` and `preview`.
 *
 * Neither `review` nor `preview` has a place in the stepper: `review` replaced the old fifth step
 * with a header button available at any moment, and `preview` (« Aperçu ») was a dialog until
 * 2026-09-11, when Akram asked for it as a real page instead — "turn it into a page not a
 * modal". Keeping both in the URL vocabulary is what makes them linkable destinations rather than
 * modal state: a reload resumes on the same view, and the readiness checklist / dossier preview
 * are both reachable by URL, not only by clicking a button in the same session.
 */
export const BUILDER_VIEWS = [...WIZARD_STEPS, "review", "preview"] as const;

export type BuilderView = (typeof BUILDER_VIEWS)[number];

/** Narrow an unknown search value to a builder view; anything else starts at the beginning. */
export function toBuilderView(value: unknown): BuilderView {
  return BUILDER_VIEWS.find((view) => view === value) ?? "general";
}

/** True when the view is one of the five stepper steps rather than the readiness or preview view. */
export function isWizardStep(view: BuilderView): view is WizardStep {
  return (WIZARD_STEPS as readonly BuilderView[]).includes(view);
}
